// Used in assign-review, resource-container, assign-review-selected-assets, clear-assign-cart.helper, selected-asset-exists.guard,
// standards-nav, student-assets.reducer, selected-assignable-sub-activity.guard, assignments.effect, resources.action

import { AssetId, EditionId } from "@carnegie-learning/common-types";
import { Asset, Bundle, CogTutorWorkspace, FilterDefinition } from "@carnegie-learning/cure-types";
import { FeathersServicePagination, FeathersServicePaginationParameters } from "@carnegie-learning/data-access-core";
import { createAction, props } from "@ngrx/store";
import {
    SelectedAssets,
} from "cli-types";

// ============== Load all Assets ============
export const LOAD_ASSETS = "[Assets] Load Assets";
export const LOAD_ASSETS_SUCCESS = "[Assets] Load Assets Success";
export const LOAD_ASSETS_FAIL = "[Assets] Load Assets Fail";

export const LoadAssets = createAction(
    LOAD_ASSETS,
    props<{
        // Adding `& CogTutorWorkspace` to add the properties for this type in order to filter on sectionId
        paginationParams: FeathersServicePaginationParameters<Asset & CogTutorWorkspace>;
    }>()
);

export const LoadAssetsSuccess = createAction(
    LOAD_ASSETS_SUCCESS,
    props<{
        payload: FeathersServicePagination<Asset>;
    }>()
);

export const LoadAssetsFail = createAction(
    LOAD_ASSETS_FAIL,
    props<{
        payload: any;
    }>()
);

// ============== Load all Assets from Ids ============
export const LOAD_ASSETS_FROM_IDS = "[Assets] Load Assets From Ids";
export const LOAD_ASSETS_SUCCESS_FROM_IDS =
    "[Assets] Load Assets From Ids Success";
export const LOAD_ASSETS_FAIL_FROM_IDS = "[Assets] Load Assets From Ids Fail";

export const LoadAssetsFromIds = createAction(
    LOAD_ASSETS_FROM_IDS,
    props<{
        assetIds: [];
    }>()
);

export const LoadAssetsFromIdsSuccess = createAction(
    LOAD_ASSETS_SUCCESS_FROM_IDS,
    props<{
        payload: FeathersServicePagination<Asset>;
    }>()
);

export const LoadAssetsFromIdsFail = createAction(
    LOAD_ASSETS_FAIL_FROM_IDS,
    props<{
        payload: any;
    }>()
);

// ============== Load selected Asset ============
export const LOAD_ASSET = "[Assets] Load Asset";
export const LOAD_ASSET_SUCCESS = "[Assets] Load Asset Success";
export const LOAD_ASSET_FAIL = "[Assets] Load Asset Fail";

export const LoadAsset = createAction(
    LOAD_ASSET,
    props<{
        assetId: AssetId;
    }>()
);

export const LoadAssetSuccess = createAction(
    LOAD_ASSET_SUCCESS,
    props<{
        payload: Asset;
    }>()
);

export const LoadAssetFail = createAction(
    LOAD_ASSET_FAIL,
    props<{
        payload: any;
    }>()
);

// ============== Remove Assets ============
export const REMOVE_ASSET = "[Asset] Remove asset";
export const REMOVE_ASSET_SUCCESS = "[Asset] Remove asset success";
export const REMOVE_ASSET_FAIL = "[Asset] Remove fail";

export const removeAsset = createAction(
    REMOVE_ASSET,
    props<{
        payload: Asset;
    }>()
);

export const removeAssetFail = createAction(
    REMOVE_ASSET_FAIL,
    props<{
        payload: any;
    }>()
);

export const removeAssetSuccess = createAction(
    REMOVE_ASSET_SUCCESS,
    props<{
        payload: Asset;
    }>()
);

// ------ Set Pagination Params -------
export const SET_ASSETS_PAGINATION_PARAMS =
    "[Content-Builder] Set Pagination Params";

export const SetAssetsPaginationParams = createAction(
    SET_ASSETS_PAGINATION_PARAMS,
    props<{
        payload: any;
    }>()
);

// ============== Set selected Assets ============
export const SET_SELECTED_ASSETS = "[Assets] Set selected Assets";

export const SetSelectedAssets = createAction(
    SET_SELECTED_ASSETS,
    props<{
        payload: SelectedAssets;
    }>()
);

// ============== Set selected Asset ============
export const SET_SELECTED_ASSET = "[Assets] Set selected Asset";

export const SetSelectedAsset = createAction(
    SET_SELECTED_ASSET,
    props<{
        payload: Asset;
    }>()
);

// ------ Update Asset ------
export const updateAsset = createAction(
    "[Assets] Update Asset",
    props<{
        payload: Asset;
    }>()
);
export const updateAssetFail = createAction(
    "[Assets] Update Asset Fail",
    props<{
        payload: any;
    }>()
); // if failure, we need constructor to send a message as the payload property back from the server
export const updateAssetSuccess = createAction(
    "[Assets] Update Asset Success",
    props<{
        payload: Asset;
    }>()
);

// ------ Create Asset ------
export const createAsset = createAction(
    "[Assets] Create Asset",
    props<{
        payload: Asset;
        createAdd?: boolean;
        createAddBundle?: boolean;
    }>()
);
export const createAssetFail = createAction(
    "[Assets] Create Asset Fail",
    props<{
        payload: any;
    }>()
); // if failure, we need constructor to send a message as the payload property back from the server
export const createAssetSuccess = createAction(
    "[Assets] Create Asset Success",
    props<{
        payload: Asset;
        createAdd?: boolean;
        createAddBundle?: boolean;
    }>()
);

// ------ Current Tab State -----
export const SET_ASSET_TAB = "[Assets] Set Asset Tab";
export const SetAssetsTab = createAction(
    SET_ASSET_TAB,
    props<{
        payload: number;
    }>()
);

// ------ Load Selected Edition Filter Value -----
export const LoadDistinctFiltersDefinitions = createAction(
    "[Assets] Load Distinct Filter Definition",
    props<{
        params: object;
        filterAttributes: FilterDefinition[];
    }>()
);

export const LoadDistinctFilterDefinitionsFail = createAction(
    "[Assets] Load Distinct Filter Definition Fail",
    props<{
        payload: any;
    }>()
);

export const LoadDistinctFilterDefinitionsSuccess = createAction(
    "[Assets] Load Distinct Filter Definition Success",
    props<{
        attributes: string[];
        payload: Array<string[]>;
    }>()
);

// ------ Load Selected Edition Filter Value -----
export const LoadDistinctPublishedByFilter = createAction(
    "[Assets] Load Distinct PublishedBy Filter",
    props<{
        resourcesTabActive: boolean;
        assignablesTabActive: boolean;
        editionId: EditionId;
    }>()
);

export const LoadDistinctPublishedByFilterFail = createAction(
    "[Assets] Load Distinct PublishedBy Filter Fail",
    props<{
        payload: any;
    }>()
);

export const LoadDistinctPublishedByFilterSuccess = createAction(
    "[Assets] Load Distinct PublishedBy Filter Success",
    props<{
        keys: string[];
        payload: Array<string[]>;
    }>()
);

export const loadedAssetsFromAlgolia = createAction(
    "[Assets] Loaded Assets From Algolia",
    props<{
        payload: Asset[];
    }>()
);

export const loadBundlesTasks = createAction(
    "[Assets] Load Bundles Tasks",
    props<{
        payload: (Asset | Bundle)[];
    }>()
);

export const loadBundlesTasksSuccess = createAction(
    "[Assets] Load Bundles Tasks Success",
    props<{
        payload: (Asset | Bundle)[];
    }>()
);

export const loadBundlesTasksFail = createAction(
    "[Assets] Load Bundles Tasks Fail",
    props<{
        payload: any;
    }>()
);

// ------ Clone Asset ------
export const cloneAsset = createAction(
    "[Assets] Clone Asset",
    props<{
        payload: Asset | Bundle;
    }>()
);
export const cloneAssetFail = createAction(
    "[Assets] Clone Asset Fail",
    props<{
        payload: any;
    }>()
); // if failure, we need constructor to send a message as the payload property back from the server
export const cloneAssetSuccess = createAction(
    "[Assets] Clone Asset Success",
    props<{
        payload: Asset | Bundle;
    }>()
);
export const clearClonedAsset = createAction("[Assets] Clear Cloned Asset");
export const clearLesson = createAction("[Assets] Clear Lesson");

// ------ Delete Asset Dialog ------
export const openedDeleteAssetDialog = createAction(
    "[Assets] Opened Delete Asset Dialog",
    props<{
        asset: Asset | Bundle;
    }>()
);
export const closedDeleteAssetDialog = createAction(
    "[Assets] Closed Delete Asset Dialog",
    props<{
        selectedAction: boolean | undefined;
        asset: Asset | Bundle;
    }>()
);
export const clearUpdatedBundle = createAction("[Assets] Clear Updated Bundle");

// ------ Edit Assigned Bundle Warning Dialog------
export const openedEditAssignedBundleWarningDialog = createAction(
    "[Assets] Opened Edit Assigned Bundle WarningD ialog",
    props<{
        asset: Bundle;
    }>()
);
export const closedEditAssignedBundleWarningDialog = createAction(
    "[Assets] Closed Edit Assigned Bundle WarningD ialog"
);
